import { useForkRef } from "@mui/material/utils";
import { AttributeTypeEnum } from "@sinch/core/entities/serverEnums";
import React, { MutableRefObject } from "react";
import { useFormContext } from "react-hook-form";
import { useMuiController } from "../../form/MuiController/MuiController";
import { BaseAttribute } from "./types";
import { CustomAttributeInputBase } from "@sinch/components/application/CustomAttributeInput/CustomAttributeInputBase";
import { match, P } from "ts-pattern";

interface CustomAttributeInputProps<
  AttributeType extends Omit<BaseAttribute, "name"> & Partial<Pick<BaseAttribute, "name">>,
  PrefixType extends string,
> {
  attribute: AttributeType;
  inputRef?: MutableRefObject<HTMLInputElement>;
  prefix?: PrefixType;
  disablePortal?: boolean;
}

export const CustomAttributeInput = <AttributeType extends BaseAttribute, PrefixType extends string = string>({
  attribute,
  prefix,
  inputRef,
  disablePortal,
}: CustomAttributeInputProps<AttributeType, PrefixType>) => {
  const { register } = useFormContext();
  const prefixWithDot = prefix ? `${prefix}.` : "";
  register(`${prefixWithDot}attributeId`, { value: attribute.id });

  const defaultValue = match(attribute.dataType)
    .with(
      P.union(
        AttributeTypeEnum.Bool,
        AttributeTypeEnum.Country,
        AttributeTypeEnum.Text,
        AttributeTypeEnum.Int,
        AttributeTypeEnum.Range,
        AttributeTypeEnum.Textarea
      ),
      () => ""
    )
    .with(P.union(AttributeTypeEnum.Choice, AttributeTypeEnum.Date, AttributeTypeEnum.File), () => null)
    .with(P.union(AttributeTypeEnum.Gallery, AttributeTypeEnum.Image, AttributeTypeEnum.Multiplechoice), () => null)
    .run();

  const {
    field: { inputRef: fieldInputRef, ...field },
  } = useMuiController({
    name: `${prefixWithDot}value`,
    defaultValue: defaultValue,
  });

  // Keep the react-hook-form ref (scroll to first invalid field) alongside the optional external ref
  const mergedInputRef = useForkRef(fieldInputRef, inputRef);

  return (
    <CustomAttributeInputBase
      {...field}
      attribute={attribute}
      disablePortal={disablePortal}
      inputRef={mergedInputRef}
    />
  );
};
